/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Avatar } from "./ui.pb";
import type { BackNavigationPrompt } from "./ui/back_navigation_prompt.pb";

/** An action to perform when the customer taps a button. */
export interface BlockerAction {
  /**
   * The title displayed on the button that the customer will use to initiate this action.
   * The iOS client requires that this field is non-empty until the app required update is newer than 4.72.
   */
  text?: string;
  localized_text?: LocalizableString;
  /** Confirmation dialog is displayed before the action is executed. */
  confirmation_dialog?:
    | BlockerActionConfirmationDialog
    | undefined;
  /**
   * Confirmation half sheet displayed before the action is executed.
   * Can be shown in Plasma Flows as of iOS 4.39, Android added support on an early version (exact min version unclear)
   */
  confirmation_sheet?:
    | BlockerActionConfirmationSheet
    | undefined;
  /** Wrapper Actions */
  delayed_action?: BlockerActionDelayedAction | undefined;
  dialog_action?: BlockerActionDialogAction | undefined;
  menu_action?:
    | BlockerActionMenuAction
    | undefined;
  /** Element Modification Actions */
  change_element_text_action?:
    | BlockerActionChangeElementTextAction
    | undefined;
  /** Execution Actions */
  end_flow_action?: BlockerActionEndFlowAction | undefined;
  open_url_action?: BlockerActionOpenURLAction | undefined;
  skip_blocker_action?: BlockerActionSkipBlockerAction | undefined;
  submit_action?: BlockerActionSubmitAction | undefined;
  internal_navigation_action?: BlockerActionInternalNavigationAction | undefined;
  sign_out_action?: BlockerActionSignOutAction | undefined;
  share_action?: BlockerActionShareFileAction | undefined;
  view_action?: BlockerActionViewFileAction | undefined;
  copy_action?: BlockerActionCopyAction | undefined;
  back_navigation_action?: BlockerActionBackNavigationAction | undefined;
  share_text_action?: BlockerActionShareTextAction | undefined;
  end_activity_action?: BlockerActionEndActivityAction | undefined;
  show_overlay_screen_action?: BlockerActionShowOverlayScreenAction | undefined;
  haptic_feedback_action?: BlockerActionHapticFeedbackAction | undefined;
  advance_action?: BlockerActionAdvanceAction | undefined;
  retreat_action?: BlockerActionRetreatAction | undefined;
}

/**
 * Describes an alert dialog that can be displayed before performing this blocker action.
 *
 * Formatting guidelines can be found here:
 * https://developer.apple.com/design/human-interface-guidelines/ios/views/alerts/
 */
export interface BlockerActionConfirmationDialog {
  /**
   * An optional short title summarizing the action being confirmed, such as “Confirm Deletion”.
   * This should be in title case.
   */
  title?: string;
  /**
   * A required message with details about the action being confirmed, such as
   * “This will permanently delete Oscar’s phone number.”
   *
   * This should use complete sentences and be in sentence (normal) case.
   */
  message?: string;
  /**
   * The text of button used to confirm the action. This should be as descriptive of the
   * action as possible, such as “Delete”. If nothing is sufficiently short then use “OK”.
   * This field is required.
   */
  confirm_button_text?: string;
  /**
   * The text of button used to cancel performing the action. This will usually be something
   * like “Cancel” or “Later”.
   * This field is required.
   */
  cancel_button_text?: string;
}

/**
 * Bottom sheet shown before blockerAction fires giving the user a message plus the option to "go back"
 * instead of executing the blockerAction. Example design:
 *  ______________________________________________________________
 * /                                                              \
 * |                              (!)                             |
 * |                                                              |
 * |         Amount is too low to complete $216.36 order          |
 * |                     Good Pour Coffee                         |
 * |                                                              |
 * | You'll need at least $25 to use Cash App Pay for this order. |
 * |                                                              |
 * |                 (Continue)    (Go Back)                      |
 * ----------------------------------------------------------------
 * Can be shown in Plasma Flows as of iOS 4.39, Android added support on an early version (exact min version unclear)
 */
export interface BlockerActionConfirmationSheet {
  /**
   * Avatar/icon displayed on the top, e.g. (!)
   *
   * Optional starting in 4.16; not optional in lower client versions.
   */
  avatar?: Avatar;
  /**
   * e.g. "Amount is too low to complete $216.36 order". Displayed below avatar.
   *
   * Not optional.
   */
  title?: string;
  /**
   * e.g. Text below title, e.g. "Good Pour Coffee".
   *
   * Optional.
   */
  subtitle?: string;
  /**
   * e.g. Main body text, e.g. "You'll need at least $25 to use Cash App Pay for this order."
   *
   * Not optional.
   */
  message?: string;
  /**
   * e.g. "Continue". Allows user to top up with the amount they selected. Tapping on the button
   * should result in the blockerAction being executed. This will be applied to the "primary"
   * green button by default.
   *
   * Optional starting in 4.16; not optional in lower client versions.
   */
  confirm_button_text?: string;
  /**
   * e.g. "Go back". Takes user back to the previous blocker/screen when pressed. This will be
   * applied to the "secondary" grey button by default.
   *
   * Optional starting in 4.40+; not optional in lower client versions.
   */
  return_button_text?: string;
  /**
   * When set to true, the primary and secondary buttons switch
   * positions but keep the positional styling, e.g.:
   *
   *                                          grey/left button          green/right button
   * ------------------------------------------------------------------------------------------
   * invert_button_treatment = false ==>      (Go Back)                 (Continue)
   * Action taken when pressed       ==>      navigates back            executes blockerAction
   * ------------------------------------------------------------------------------------------
   * invert_button_treatment = true  ==>      (Continue)                (Go Back)
   * Action taken when pressed       ==>      executes blockerAction    navigates back
   *
   * Optional on iOS, Android from 4.39+ (not optional on earlier Android versions), defaults to false.
   */
  invert_button_treatment?: boolean;
  /** The action to perform after scheduled_action_delay_ms. */
  scheduled_action?: BlockerAction;
  /**
   * The amount of time to wait before performing scheduled_action.
   * Deprecated: Assign a BlockerAction.DelayedAction to scheduled_action instead.
   */
  scheduled_action_delay_ms?: number;
  /** Indicates which confirmation sheet variant should be shown */
  variant?: BlockerActionConfirmationSheetVariant;
}

/** Set of variants used to indicate which confirmation sheet variant should be shown */
export enum BlockerActionConfirmationSheetVariant {
  CONTROL = "CONTROL",
  VARIANT_A = "VARIANT_A",
  VARIANT_C = "VARIANT_C",
}

/**
 * Executes a BlockerAction after a specified delay
 *
 * Client Version Availability:
 * iOS - 4.90
 * Android - 4.92
 *
 * go/delayed-actions
 */
export interface BlockerActionDelayedAction {
  /**
   * Optional. Duration in ms that the client should wait on the
   * blocker screen before executing the action (default is 1000ms).
   */
  delay_ms?: number;
  /** Required. Defines what action to take after the specified waiting time. */
  action?: BlockerAction;
}

/**
 * Preemptively dismisses the current flow on the client.
 *  The server won't be notified that this happened unless you set `notify_end_flow_id`
 */
export interface BlockerActionEndFlowAction {
  result?: BlockerActionEndFlowActionResult;
  /**
   * If not set, no interaction is made with the server.
   * If set, a call will be made to the `end-flow` endpoint in the background
   * after dismissing the flow with this as the `EndFlowRequest#action_id`.
   * Min supported app versions: iOS 3.72.0, Android 3.70
   */
  notify_end_flow_id?: string;
}

export enum BlockerActionEndFlowActionResult {
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
}

/** Displays a sheet to the customer with a list of additional options. */
export interface BlockerActionMenuAction {
  actions?: BlockerAction[];
}

/**
 * Opens the given URL.
 *
 * The URL should be opened in a web browser.
 *
 * If end_flow is false or not provided, the flow will be left intact (not dismissed).
 * NOTE: If the URL is a client route, end_flow will be ignored.
 * Routes in this scenario will dismiss dependent on client-side logic.
 * Additional context: https://www.notion.so/cashappcash/Flow-and-Client-Routes-23854e8e103180d7a4e3e212105ddd63
 */
export interface BlockerActionOpenURLAction {
  /** The URL to open. */
  url?: string;
  /**
   * True if the flow should be ended after handling the action.
   * NOTE: If the URL is a client route, this parameter will be ignored.
   * Routes in this scenario will dismiss dependent on client-side logic.
   * Additional context: https://www.notion.so/cashappcash/Flow-and-Client-Routes-23854e8e103180d7a4e3e212105ddd63
   */
  end_flow?: boolean;
  /** Defines how the URL should be opened. */
  behavior?: BlockerActionOpenURLActionBehavior;
}

export enum BlockerActionOpenURLActionBehavior {
  /**
   * DEFAULT - On Android, this Behavior causes the URL to open in a Custom Tab
   * (see: https://developer.chrome.com/docs/android/custom-tabs/overview/).
   * On iOS, the URL will be opened via an external browser.
   */
  DEFAULT = "DEFAULT",
  /** REDIRECT - On both iOS and Android, this Behavior causes the URL to open via an external browser. */
  REDIRECT = "REDIRECT",
}

/**
 * Moves to the next blocker without server interaction.
 * If used from a FormBlocker presented using ShowOverlayScreenAction this action will dismiss the overlay
 */
export interface BlockerActionSkipBlockerAction {
}

/**
 * Calls the `submit-form` end point (which will update the scenario plan), then advances to
 * the next blocker.
 *
 * The `id` value should be included in the `SubmitFormRequest` when calling `submit-form`.
 */
export interface BlockerActionSubmitAction {
  id?: string;
  /**
   * If set, the client will display this label along side the loading spinner while it's waiting
   * for the server's response. Min supported app versions: iOS 4.35.0, Android 3.33
   */
  loading_label?: string;
  /**
   * The direction that the client should animate towards when displaying the loading spinner and
   * resulting server-provided blocker. Added in version 3.43. Defaults to FORWARD.
   */
  direction?: BlockerActionSubmitActionAnimationDirection;
}

export enum BlockerActionSubmitActionAnimationDirection {
  FORWARD = "FORWARD",
  BACKWARD = "BACKWARD",
}

/** Navigates to a different part of the app. */
export interface BlockerActionInternalNavigationAction {
  /** @deprecated */
  support?: BlockerActionInternalNavigationActionSupport | undefined;
  client_route?: BlockerActionInternalNavigationActionClientRoute | undefined;
  necessity?: BlockerActionInternalNavigationActionNecessity;
}

/**
 * Describes how important it is for a navigation action to be honored by the client.
 * The default is MANDATORY.
 */
export enum BlockerActionInternalNavigationActionNecessity {
  /** MANDATORY - The client must honor this navigation, regardless of other context. */
  MANDATORY = "MANDATORY",
  /** PREFERRED - The client may choose to ignore this navigation and go somewhere else instead. */
  PREFERRED = "PREFERRED",
}

/**
 * Deprecated. Please use InternalNavigationAction.ClientRoute instead.
 * - To link to specific support article, use ViewSupportNode client route.
 * - To link to general support, use ViewSupportHome client route.
 */
export interface BlockerActionInternalNavigationActionSupport {
  support_flow_node?: string;
}

/** Performs a client route. */
export interface BlockerActionInternalNavigationActionClientRoute {
  url?: string;
}

/** Abandons any flows in progress and signs the user out. */
export interface BlockerActionSignOutAction {
}

/** Opens a client native share sheet. */
export interface BlockerActionShareFileAction {
  file_url?: string;
  title?: string;
  extension?: string;
  loading_text?: string;
  options?: BlockerActionShareFileActionShareOption[];
}

export interface BlockerActionShareFileActionEmailOption {
  subject?: string;
  body?: string;
}

export interface BlockerActionShareFileActionViewOption {
}

export interface BlockerActionShareFileActionShareOption {
  email?: BlockerActionShareFileActionEmailOption | undefined;
  view?: BlockerActionShareFileActionViewOption | undefined;
}

/**
 * Opens a client native share sheet for sharing text data.
 * Please note the email_subject is skipped on Samsung devices with app version >= 3.78.
 * See CASHA-10660 for more details.
 */
export interface BlockerActionShareTextAction {
  /** Required. The actual text data to share (ie. via SMS, email, etc) */
  text_data?: string;
  /** This optional title is used for a preview when sharing */
  title?: string;
  /**
   * This optional email subject is used if the customer selects email sharing
   * in the share sheet, except on Samsung devices with app version >= 3.78.
   */
  email_subject?: string;
  /** when populated, the app will open the sms app with message populated with text_data and recipient populated with sms_recipient. */
  sms_recipient?: string;
  /**
   * DO NOT USE this field as it's on a deprecation path. use after_successful_sms_blocker_action
   * instead. This field only works for iOS devices.The action triggered after getting back from
   * the sms app to the app (ios only)
   */
  after_successful_sms_action?: BlockerActionSubmitAction;
  /**
   * The is a new version of the `after_successful_sms_action` that allows a more general
   * BlockerAction as a type and will handle both on iOS and Android
   */
  after_successful_sms_blocker_action?: BlockerAction;
}

/** Opens a file with a client viewer. */
export interface BlockerActionViewFileAction {
  file_url?: string;
  title?: string;
  extension?: string;
  loading_text?: string;
}

/**
 * Displays a dialog.
 * This can eventually replace the existing confirmation dialog.
 */
export interface BlockerActionDialogAction {
  /** Required. */
  title?: string;
  /** Required. */
  message?: string;
  /** Required. */
  primary_button?: BlockerActionDialogActionButton;
  /**
   * Optional. If the button's action is nil the client ignores the button's text and
   * style and assumes this is a cancel button.
   */
  secondary_button?: BlockerActionDialogActionButton;
}

export interface BlockerActionDialogActionButton {
  /** Required. */
  text?: string;
  /**
   * Optional. If set, this action will be called when the `primary_button` is tapped.
   * If null then this button simply dismisses the dialog.
   */
  action?: BlockerAction;
  /** Optional. Client support with a version >= 4.32. If not set client will use DEFAULT. */
  style?: BlockerActionDialogActionButtonStyle;
}

export enum BlockerActionDialogActionButtonStyle {
  DEFAULT = "DEFAULT",
  CANCEL = "CANCEL",
  DESTRUCTIVE = "DESTRUCTIVE",
}

/** A blocker action that will allow the user to copy text to their clipboard. */
export interface BlockerActionCopyAction {
  /** The (non-optional) text to be copied to the clipboard when the action is performed. */
  text?: string;
}

/**
 * [Experimental] A blocker action that causes the mobile client to minimize Cash App and navigate
 * back to the previous application on their mobile OS app stack.
 */
export interface BlockerActionBackNavigationAction {
  /** A fallback URL clients can use for forward navigation if it needs to do so for any reason. */
  redirect_url?:
    | string
    | undefined;
  /** Represents a screen prompting the user to manually navigate back. */
  back_navigation_prompt?: BackNavigationPrompt | undefined;
}

/**
 * [Experimental] A blocker action that causes the mobile client to close Cash App when it is
 * running as an Android Activity within another app. Returns control back to the primary app.
 */
export interface BlockerActionEndActivityAction {
  /**
   * Provides Android Activity Result Status Value.
   *
   * Using an explicit object to prevent accidental defaults (e.g. int32 default (0) would
   * unintentionally communicate a RESULT_CANCELLED status if unset).
   */
  end_activity_result_status?: BlockerActionEndActivityResult;
  /**
   * Map of key/vals that the client will set onto the Android Intent prior to returning to
   * to the primary calling app.
   * ex: [Android.Intent.putExtra()](https://developer.android.com/reference/android/content/Intent#putExtra(java.lang.String,%20char))
   */
  intent_resolution_data?: { [key: string]: string };
}

export interface BlockerActionEndActivityActionIntentResolutionDataEntry {
  key: string;
  value: string;
}

/** Provides Android Activity Result Status Value */
export interface BlockerActionEndActivityResult {
  /**
   * Corresponds to an Android Activity Result Status integer value. Cash client will terminate
   * the Cash App activity with the value designated. This value will be passed back to the
   * primary calling app.
   * ex:
   * - (-1) -> [RESULT_OK](https://developer.android.com/reference/android/app/Activity#RESULT_OK)
   * - (1) -> [RESULT_CANCELLED][https://developer.android.com/reference/android/app/Activity#RESULT_CANCELED]
   */
  result_status?: number;
}

/**
 * [Experimental] A blocker action that allows the mobile client to react to changes on a blocker
 * and dynamically update the text field of a target. Visit go/dynamic-formblocker for more details.
 */
export interface BlockerActionChangeElementTextAction {
  /** Required. Specifies the string to update the target text to. */
  text?: string;
  primary_action_target?: BlockerActionChangeElementTextActionPrimaryActionTarget | undefined;
  secondary_action_target?: BlockerActionChangeElementTextActionSecondaryActionTarget | undefined;
  element_target?: BlockerActionChangeElementTextActionElementTarget | undefined;
}

/** Primary action does not have an element id and cannot be targetted by id. */
export interface BlockerActionChangeElementTextActionPrimaryActionTarget {
}

/** Secondary action does not have an element id and cannot be targetted by id. */
export interface BlockerActionChangeElementTextActionSecondaryActionTarget {
}

/** Used to target any element. */
export interface BlockerActionChangeElementTextActionElementTarget {
  /** Required. */
  element_id?: string;
}

/** Action to present a new FormBlocker from the current FormBlocker */
export interface BlockerActionShowOverlayScreenAction {
  subform_id?: string;
}

/** A blocker action that will trigger haptic feedback. */
export interface BlockerActionHapticFeedbackAction {
  type?: BlockerActionHapticFeedbackActionType;
}

export enum BlockerActionHapticFeedbackActionType {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

/**
 * Advances to the specified blocker ID.
 * This will push a new blocker onto the client's stack.
 *
 * **Highly experimental!**
 * Don't use unless you're working on the improved Flows navigation spike:
 * https://www.notion.so/cashappcash/Design-improved-Flows-navigation-2be54e8e1031808b8524f8b63d54a768
 */
export interface BlockerActionAdvanceAction {
  /**
   * The ID of the blocker to advance to.
   * It is invalid to specify an ID that has not been given to the client previously.
   *
   * @contract:required=true
   * @contract:not_empty=true
   */
  blocker_id?: string;
}

/**
 * Retreats to the specified blocker ID.
 * This will pop blockers off the client's stack until the specified ID is found.
 *
 * **Highly experimental!**
 * Don't use unless you're working on the improved Flows navigation spike:
 * https://www.notion.so/cashappcash/Design-improved-Flows-navigation-2be54e8e1031808b8524f8b63d54a768
 */
export interface BlockerActionRetreatAction {
  /**
   * The ID of the blocker to retreat to.
   * It is invalid to specify an ID that has not been given to the client previously.
   *
   * @contract:required=true
   * @contract:not_empty=true
   */
  blocker_id?: string;
}
